/*
 ** Author: Mika Dekker (AskMario)
 ** Date: 09/2025
 ** Test: GLX-009 Cart item checkout nudge
 ** Variation: AskMario
*/

// Cart Reminder System with AJAX
class CartReminder {
    constructor() {
        this.reminderShownKey = 'cart_reminder_shown_today';
        this.lastCartCountKey = 'cart_reminder_last_count';
        this.reminderElement = null;
        this.cartData = null;
        this.isChecking = false;

        // Suppress the reminder when the homepage is the entry page of this session
        // (so returning visitors with a cart aren't nagged before they start shopping).
        this.isHomepageLanding = this.computeHomepageLanding();

        // Initialize on page load
        this.init();
    }

    computeHomepageLanding() {
        try {
            const path = window.location.pathname;
            const isHome = path === '/' || path === '';
            const navKey = 'cart_reminder_has_navigated';
            const hasNavigated = sessionStorage.getItem(navKey) === 'true';
            // Mark that the session has now loaded at least one page.
            sessionStorage.setItem(navKey, 'true');
            // Landing on the homepage as the first page of the session -> suppress.
            return isHome && !hasNavigated;
        } catch (e) {
            return false;
        }
    }

    init() {
        console.log('Cart Reminder: Initializing...');

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                console.log('Cart Reminder: DOM loaded, checking cart...');
                this.checkAndShowReminder();
            });
        } else {
            console.log('Cart Reminder: DOM already loaded, checking cart...');
            this.checkAndShowReminder();
        }
    }

    // Fetch cart items using AJAX
    async getCartItems() {
        if (this.isChecking) return this.cartData;
        this.isChecking = true;

        try {
            const response = await fetch('/cart.js', {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const cartData = await response.json();
            console.log('Cart Contents:', cartData);

            this.cartData = cartData;
            this.isChecking = false;
            return cartData;
        } catch (error) {
            console.error('Error fetching cart:', error);
            this.isChecking = false;
            return null;
        }
    }

    async hasCartItems() {
        try {
            const cartData = await this.getCartItems();
            if (!cartData) return false;

            const items = cartData.items || [];
            const hasItems = items.length > 0;

            console.log(hasItems ? `There are ${items.length} items in the cart.` : 'The cart is empty.');
            return hasItems;
        } catch (error) {
            console.warn('Cart Reminder: Error checking cart items', error);
            return false;
        }
    }

    shouldShowOnCurrentPage() {
        const currentPath = window.location.pathname.toLowerCase();
        console.log('Cart Reminder: Current path:', currentPath);

        if (currentPath.includes('/products/') || currentPath.match(/\/products\/?$/)) return false;
        if (currentPath.includes('/collections/') || currentPath.match(/\/collections\/?$/)) return false;
        if (currentPath.includes('/cart') || currentPath.includes('/checkout')) return false;

        return true;
    }

    getLastCartCount() {
        const stored = sessionStorage.getItem(this.lastCartCountKey);
        return stored ? parseInt(stored, 10) : 0;
    }

    setLastCartCount(count) {
        sessionStorage.setItem(this.lastCartCountKey, count.toString());
    }

    hasReminderBeenShown() {
        return sessionStorage.getItem(this.reminderShownKey) === 'true';
    }

    markReminderAsShown() {
        sessionStorage.setItem(this.reminderShownKey, 'true');
    }

    shouldShowReminder(currentItemCount) {
        // If reminder hasn't been shown yet, show it
        if (!this.hasReminderBeenShown()) {
            console.log('Cart Reminder: Never shown before, will show');
            return true;
        }

        // If reminder has been shown, only show again if cart count increased
        const lastCount = this.getLastCartCount();
        console.log(`Cart Reminder: Last count: ${lastCount}, Current count: ${currentItemCount}`);
        
        if (currentItemCount > lastCount) {
            console.log('Cart Reminder: Cart count increased, will show');
            return true;
        }

        console.log('Cart Reminder: No need to show');
        return false;
    }

    getCartSummary() {
        if (!this.cartData || !this.cartData.items) return null;

        const items = this.cartData.items;
        const itemCount = items.length;
        const totalPrice = this.cartData.total_price || 0;
        const formattedPrice = (totalPrice / 100).toFixed(2);

        return {
            itemCount,
            totalPrice: formattedPrice,
            currency: this.cartData.currency || 'USD'
        };
    }

    async checkAndShowReminder() {
        try {
            if (!this.shouldShowOnCurrentPage()) return;
            if (this.isHomepageLanding) return;
            if (this.reminderElement) return;

            const hasItems = await this.hasCartItems();
            if (!hasItems) {
                // No items in cart, reset tracking
                this.setLastCartCount(0);
                return;
            }

            const cartSummary = this.getCartSummary();
            const currentItemCount = cartSummary ? cartSummary.itemCount : 0;

            if (this.shouldShowReminder(currentItemCount)) {
                this.showReminder();
                this.markReminderAsShown();
                this.setLastCartCount(currentItemCount);
            }
        } catch (error) {
            console.error('Cart Reminder: Error in checkAndShowReminder', error);
        }
    }

    showReminder() {
        const cartSummary = this.getCartSummary();

        this.reminderElement = document.createElement('div');
        this.reminderElement.className = 'cart-reminder-popup';
        this.reminderElement.setAttribute('role', 'dialog');
        this.reminderElement.setAttribute('aria-live', 'polite');
        this.reminderElement.setAttribute('aria-labelledby', 'cart-reminder-title');

        let itemText = "Don't forget to checkout";
        let descriptionText = 'You have items waiting in your cart.';

        if (cartSummary) {
            const { itemCount, totalPrice, currency } = cartSummary;
            descriptionText = `${itemCount} item${itemCount > 1 ? 's' : ''} worth ${currency} ${totalPrice}`;
        }

        // Enrich the reminder with the first cart item's image
        let imageMarkup = '';
        const items = (this.cartData && this.cartData.items) || [];
        if (items.length > 0) {
            const firstItem = items[0];
            let imageUrl = (firstItem.featured_image && firstItem.featured_image.url) || firstItem.image || '';
            if (imageUrl) {
                imageUrl += (imageUrl.indexOf('?') > -1 ? '&' : '?') + 'width=200';
                const rawAlt = (firstItem.featured_image && firstItem.featured_image.alt) || firstItem.product_title || firstItem.title || '';
                const imageAlt = String(rawAlt).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
                imageMarkup =
                    '<div class="cart-reminder-image">' +
                        '<img src="' + imageUrl + '" alt="' + imageAlt + '" loading="lazy">' +
                    '</div>';
            }
        }

        this.reminderElement.innerHTML =
            '<div class="cart-reminder-content">' +
                '<button class="cart-reminder-close" aria-label="Close reminder">×</button>' +
                '<div class="cart-reminder-main">' +
                    imageMarkup +
                    '<div class="cart-reminder-body">' +
                        '<div class="cart-reminder-text">' +
                            '<h4 id="cart-reminder-title">' + itemText + '</h4>' +
                            '<p>' + descriptionText + '</p>' +
                        '</div>' +
                        '<div class="cart-reminder-actions">' +
                            '<button class="cart-reminder-checkout" aria-label="Go to checkout">Complete Checkout</button>' +
                            '<button class="cart-reminder-continue" aria-label="Continue shopping">Continue Shopping</button>' +
                        '</div>' +
                    '</div>' +
                '</div>' +
            '</div>';

        document.body.appendChild(this.reminderElement);
        this.addEventListeners();

        setTimeout(() => this.reminderElement.classList.add('cart-reminder-show'), 100);

        setTimeout(() => {
            if (this.reminderElement && !this.reminderElement.classList.contains('cart-reminder-interacted')) {
                this.hideReminder();
            }
        }, 12000);
    }

    addEventListeners() {
        if (!this.reminderElement) return;

        const checkoutBtn = this.reminderElement.querySelector('.cart-reminder-checkout');
        const continueBtn = this.reminderElement.querySelector('.cart-reminder-continue');
        const closeBtn = this.reminderElement.querySelector('.cart-reminder-close');

        checkoutBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.reminderElement.classList.add('cart-reminder-interacted');
            this.goToCheckout();
        });

        continueBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.reminderElement.classList.add('cart-reminder-interacted');
            this.continueShopping();
        });

        closeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.reminderElement.classList.add('cart-reminder-interacted');
            this.hideReminder();
        });

        this.reminderElement.addEventListener('click', (e) => {
            if (e.target === this.reminderElement) {
                this.reminderElement.classList.add('cart-reminder-interacted');
                this.hideReminder();
            }
        });

        const escapeHandler = (e) => {
            if (e.key === 'Escape' && this.reminderElement) {
                this.reminderElement.classList.add('cart-reminder-interacted');
                this.hideReminder();
                document.removeEventListener('keydown', escapeHandler);
            }
        };

        document.addEventListener('keydown', escapeHandler);
    }

    goToCheckout() {
        window.location.href = '/checkout';
        this.hideReminder();
    }

    continueShopping() {
        window.location.href = '/';
        this.hideReminder();
    }

    hideReminder() {
        if (this.reminderElement) {
            this.reminderElement.classList.remove('cart-reminder-show');
            this.reminderElement.classList.add('cart-reminder-hide');

            setTimeout(() => {
                if (this.reminderElement?.parentNode) {
                    this.reminderElement.parentNode.removeChild(this.reminderElement);
                }
                this.reminderElement = null;
            }, 300);
        }
    }

    async checkCart() {
        await this.checkAndShowReminder();
    }

    resetReminder() {
        sessionStorage.removeItem(this.reminderShownKey);
        sessionStorage.removeItem(this.lastCartCountKey);
        this.hideReminder();
        this.cartData = null;
    }

    async forceShowReminder() {
        this.resetReminder();
        await this.checkAndShowReminder();
    }

    destroy() {
        this.hideReminder();
        this.cartData = null;
    }
}

// Initialize
let cartReminder;
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        cartReminder = new CartReminder();
        window.CartReminder = cartReminder;
    });
} else {
    cartReminder = new CartReminder();
    window.CartReminder = cartReminder;
}

// Theme events - only reset and check when items are added
document.addEventListener('cart:updated', async () => {
    if (cartReminder) {
        cartReminder.cartData = null; // Clear cached cart data
        await cartReminder.checkCart();
    }
});

document.addEventListener('cart:added', async () => {
    if (cartReminder) {
        cartReminder.cartData = null; // Clear cached cart data
        await cartReminder.checkCart();
    }
});

window.addEventListener('popstate', async () => {
    if (cartReminder) {
        setTimeout(async () => {
            await cartReminder.checkCart();
        }, 500);
    }
});

window.addEventListener('focus', async () => {
    if (cartReminder && cartReminder.shouldShowOnCurrentPage()) {
        await cartReminder.checkCart();
    }
});